import { motion, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useRef, useState } from "react";

/**
 * Two-layer premium cursor: instant dot + lagged ring.
 *
 * Perf:
 * - Mounts only on hover+fine pointer, no reduced-motion
 * - rAF-throttled pointermove; hover state mutated via DOM dataset (no React re-render per move)
 * - Auto-hides when window loses focus or tab hidden
 *
 * A11y:
 * - Hides itself the moment the user presses Tab so native focus rings are visible
 *   and keyboard users keep their default cursor experience.
 */
export const PremiumCursor = () => {
  const [active, setActive] = useState(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const rx = useSpring(x, { stiffness: 240, damping: 26, mass: 0.5 });
  const ry = useSpring(y, { stiffness: 240, damping: 26, mass: 0.5 });
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hoverMQ = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reduceMQ = window.matchMedia("(prefers-reduced-motion: reduce)");
    const compute = () => setActive(hoverMQ.matches && !reduceMQ.matches);
    compute();
    hoverMQ.addEventListener?.("change", compute);
    reduceMQ.addEventListener?.("change", compute);
    return () => {
      hoverMQ.removeEventListener?.("change", compute);
      reduceMQ.removeEventListener?.("change", compute);
    };
  }, []);

  useEffect(() => {
    if (!active) return;
    document.documentElement.classList.add("has-premium-cursor");

    let raf = 0;
    let nextX = 0;
    let nextY = 0;
    let nextHover = false;
    let lastHover = false;

    const flush = () => {
      x.set(nextX);
      y.set(nextY);
      if (nextHover !== lastHover) {
        lastHover = nextHover;
        wrapRef.current?.setAttribute("data-hover", nextHover ? "1" : "0");
      }
      raf = 0;
    };
    const move = (e: PointerEvent) => {
      nextX = e.clientX;
      nextY = e.clientY;
      const t = e.target as HTMLElement | null;
      nextHover = !!t?.closest(
        'a, button, [role="button"], [data-cursor="hover"], input, textarea, select, label'
      );
      if (!raf) raf = requestAnimationFrame(flush);
    };
    const hide = () => {
      x.set(-100);
      y.set(-100);
      wrapRef.current?.setAttribute("data-visible", "0");
    };
    const show = () => wrapRef.current?.setAttribute("data-visible", "1");
    // Keyboard takeover — restore native cursor immediately
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") document.documentElement.classList.remove("has-premium-cursor");
    };
    const onMouseDown = () => document.documentElement.classList.add("has-premium-cursor");
    const onBlur = () => hide();
    const onVis = () => (document.hidden ? hide() : show());

    window.addEventListener("pointermove", move, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("blur", onBlur);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("visibilitychange", onVis);
      cancelAnimationFrame(raf);
      document.documentElement.classList.remove("has-premium-cursor");
    };
  }, [active, x, y]);

  if (!active) return null;

  return (
    <div ref={wrapRef} data-visible="1" data-hover="0" className="premium-cursor" aria-hidden="true">
      <motion.div
        style={{ x: rx, y: ry }}
        className="pointer-events-none fixed top-0 left-0 z-[80] -translate-x-1/2 -translate-y-1/2"
      >
        <div className="premium-cursor-ring size-8 rounded-full border border-foreground/35 mix-blend-difference transition-[transform,opacity] duration-200 ease-out" />
      </motion.div>
      <motion.div
        style={{ x, y }}
        className="pointer-events-none fixed top-0 left-0 z-[81] -translate-x-1/2 -translate-y-1/2"
      >
        <div className="premium-cursor-dot size-1.5 rounded-full bg-accent mix-blend-difference transition-[transform,opacity] duration-150 ease-out" />
      </motion.div>
    </div>
  );
};
